<template>
  <v-app class="app-ps">
    <EmbedHeader :company="company" v-if="isEmbeddedNewTabJourney" />
    <TheHeader
      v-if="!isTenantsJourney && !isEmbeddedNewTabJourney"
      @openWhyChooseUsDrawer="openWhyChooseUsDrawer"
    />

    <!-- app navigation -->
    <v-main>
      <keep-alive include="FactFind">
        <router-view />
      </keep-alive>
      <v-snackbar v-model="snackbar" :timeout="timeoutLimit">
        <span v-if="!onlineStatus"
          ><v-icon color="white" class="mr-2">mdi-cloud-off-outline</v-icon
          >You're offline. Trying to reconnect...</span
        >
        <span v-if="onlineStatus"
          ><v-icon color="green" class="mr-2">mdi-check-circle</v-icon>You're
          back online</span
        >
      </v-snackbar>

      <v-speed-dial
        bottom
        left
        fixed
        direction="top"
        open-on-hover
        transition="slide-y-reverse-transition"
        v-if="env === 'development'"
      >
        <template v-slot:activator>
          <v-btn color="accent" dark fab @click="populateDummyData('all')">
            <v-icon> mdi-auto-fix </v-icon>
          </v-btn>
        </template>
        <v-btn dark small color="accent" @click="populateDummyData('1')">
          0-1
        </v-btn>
      </v-speed-dial>

      <SessionExpiredDialog :isDialogOpen="isShowingD2CSessionTimeoutDialog" />
      <BaseDialogContents />
    </v-main>
    <EmbedFooter v-if="isEmbeddedNewTabJourney" />
    <TheFooter v-if="isShowFooter && !isEmbeddedNewTabJourney" />
    <CookieBanner v-if="showCookieBanner" />
    <v-overlay :value="showAppOverlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <BaseDialog
      v-model="isShowSaveAndExitDialog"
      headerIcon="mdi-content-save"
      headerIconColor="#106175"
      title="Save &amp; exit"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-5 mb-n1"
      headerDivider
      dark2Icon
      :max-width="620"
      @dialogCloseButton="toggleGlobalSaveAndExitDialog"
      @click:outside="toggleGlobalSaveAndExitDialog"
    >
      <template v-slot:content="{}">
        <SaveAndExit
          :sectionReminder="isSelfServe ? SAVE_AND_EXIT_REMINDER : ''"
          :showUserRef="!isSelfServe && (isQuestionSteps || isQuoteResultPage)"
          @saveAndExit="saveAndExit"
          @close="toggleGlobalSaveAndExitDialog"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      v-model="showQuoteExpiredDialog"
      title="Quote expired"
      windowClass="pa-0 pb-xl-0"
      headerClass="px-10 py-8 mb-n1"
      headerDivider
      :max-width="550"
    >
      <template v-slot:content="{}">
        <DialogContentQuoteExpired
          @close="toggleQuoteExpiredDialog(false)"
          :selectedQuote="selectedQuote"
        />
      </template>
    </BaseDialog>

    <DrawerWhyChooseUs
      v-model="isShowDrawerWhyChooseUs"
      @closeWhyChooseUsDrawer="closeWhyChooseUsDrawer"
    />
  </v-app>
</template>

<script>
import sessionService from "@/services/session.service";
import { mapGetters, mapActions, mapState } from "vuex";
import SessionExpiredDialog from "@/views/Dialog/SessionExpiredDialog";

import TheFooter from "@/components/TheFooter.vue";
import TheHeader from "@/components/TheHeader.vue";
import SaveAndExit from "@/views/Dialog/SaveAndExit";
import CookieBanner from "@/components/CookieBanner.vue";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import EmbedHeader from "@/components/EmbedJourney/EmbedHeader.vue";
import EmbedFooter from "@/components/EmbedJourney/EmbedFooter.vue";
import BaseDialogContents from "@/components/BaseComponents/BaseDialogContents.vue";
import DrawerWhyChooseUs from "@/components/QuoteResult/DrawerWhyChooseUs.vue";

import {
  ROUTE_URL,
  ROUTE_NAME,
  PATHS_ARRAY_EXCLUDE_CHECK_UNSAVED_DATA,
} from "./constants/route.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { SAVE_AND_EXIT_REMINDER } from "@/constants/message.constants";
import { LIST_PAGE_D2C_SHOW_FOOTER } from "@/constants/route.constants";
import { isEmpty } from "@/common/utils";
import { getLandingPageLink } from "@/common/navigating";
import { isQuoteExpired } from "@/common/quotes";

export default {
  name: "App",
  components: {
    TheHeader,
    TheFooter,
    EmbedHeader,
    EmbedFooter,
    SessionExpiredDialog,
    BaseDialogContents,
    CookieBanner,
    BaseDialog,
    SaveAndExit,
    DrawerWhyChooseUs,
    DialogContentQuoteExpired: () =>
      import("@/components/QuoteResult/DialogContentQuoteExpired"),
  },
  data() {
    return {
      isShowSaveAndExitDialog: false,
      showQuoteExpiredDialog: false,
      onlineStatus: navigator.onLine,
      showBackOnline: false,
      snackbar: false,
      env: process.env.NODE_ENV,
      d2cIntervalId: null,
      isShowingD2CSessionTimeoutDialog: false,
      company: null,
      acceptedCookie: JSON.parse(localStorage.getItem("acceptedCookie")),
      SAVE_AND_EXIT_REMINDER,
      isShowDrawerWhyChooseUs: false,
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "currentPath",
      "isShowGlobalSaveAndExitDialog",
      "isShowQuoteExpiredDialog",
      "showAppOverlay",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "quoteAccessId",
      "isSelfServe",
      "getCurrentRefObserverForm",
      "isQuestionSteps",
      "isQuoteResultPage",
    ]),
    ...mapGetters(APPLICATION_MODULE, ["selectedQuote"]),
    timeoutLimit() {
      return this.onlineStatus ? 5000 : 0;
    },
    currentRouteName() {
      return this.$route.name;
    },
    isShowFooter() {
      return (
        this.isSelfServe ||
        LIST_PAGE_D2C_SHOW_FOOTER.includes(this.currentRouteName)
      );
    },
    isTenantsJourney() {
      return this.$route.path.includes(ROUTE_URL.TENANTS_B2B);
    },
    isEmbeddedNewTabJourney() {
      return this.company;
    },
    isCurrentRouteNeedShowCookie() {
      return (
        this.quoteAccessId ||
        this.isSelfServe ||
        this.currentRouteName === ROUTE_NAME.HOME.D2C.LANDING_PAGE ||
        this.currentRouteName === ROUTE_NAME.LANDLORDS.D2C.LANDING_PAGE
      );
    },
    isCompletePage() {
      return (
        this.$route.name === "Complete" || this.$route.meta.isComplete === true
      );
    },
    showCookieBanner() {
      return (
        !this.acceptedCookie &&
        this.isCurrentRouteNeedShowCookie &&
        !this.company
      );
    },
  },
  onIdle() {
    if (this.quoteAccessId) {
      this.showSessionExpiredDialog();
    }
  },
  onActive() {
    this.d2cSessionInterval = setInterval(() => {
      if (
        this.$route.name != ROUTE_NAME.HOME.D2C.LANDING_PAGE &&
        this.$route.name != ROUTE_NAME.LANDLORDS.D2C.LANDING_PAGE &&
        this.quoteAccessId
      ) {
        sessionService
          .refreshD2CSession()
          .then((response) => {
            return response;
          })
          .catch((error) => {
            if (error && error.response && error.response.status == 401) {
              console.log(error);
              this.showSessionExpiredDialog();
            }
          });
      }
    }, 60000);
  },

  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "toggleQuoteExpiredDialog",
      "saveAndExit",
      "setCurrentPath",
    ]),
    populateDummyData(step) {
      if (step === "all") {
        window.populateDummyData();
      }
      if (step === "1") {
        window.populateDummyDataStepZeroToOne();
      }
    },
    updateOnlineStatus(e) {
      const { type } = e;
      this.onlineStatus = type === "online";
    },
    showSessionExpiredDialog() {
      if (!this.isShowingD2CSessionTimeoutDialog) {
        this.isShowingD2CSessionTimeoutDialog = true;
        clearInterval(this.d2cSessionInterval);
        window.onbeforeunload = null; //to remove the additional popup of "Leave Site"
        const landingPageUrl = getLandingPageLink();
        setTimeout(() => {
          window.location.href = landingPageUrl;
        }, 30000);
      }
    },
    getEmbedCompanyLogo() {
      let URLParams = new URLSearchParams(window.location.search);
      return URLParams.get("company");
    },
    openWhyChooseUsDrawer() {
      this.isShowDrawerWhyChooseUs = true;
    },
    closeWhyChooseUsDrawer() {
      this.isShowDrawerWhyChooseUs = false;
    },
  },
  watch: {
    onlineStatus(v) {
      if (v) {
        this.showBackOnline = true;
        setTimeout(() => {
          this.showBackOnline = false;
        }, 5000);
      } else {
        this.snackbar = true;
      }
    },
    isShowGlobalSaveAndExitDialog(value) {
      this.isShowSaveAndExitDialog = value;
    },
    isShowQuoteExpiredDialog(value) {
      this.showQuoteExpiredDialog = value;
    },
    "$route.path": function () {
      this.setCurrentPath(this.$route.path);
    },
    isQuestionSteps(value) {
      // recheck quote expiry date any time we move from question stage to application stage
      if (
        value === false &&
        !this.isCompletePage &&
        !isEmpty(this.selectedQuote) &&
        isQuoteExpired(this.selectedQuote)
      ) {
        this.toggleQuoteExpiredDialog(true);
      }
    },
  },
  mounted() {
    this.company = this.getEmbedCompanyLogo();
    if (this.company) {
      document.body.classList.add("hide-live-chat");
    }

    window.addEventListener("online", this.updateOnlineStatus);
    window.addEventListener("offline", this.updateOnlineStatus);
  },
  beforeDestroy() {
    window.removeEventListener("online", this.updateOnlineStatus);
    window.removeEventListener("offline", this.updateOnlineStatus);
  },
  created() {
    window.onbeforeunload = function (event) {
      const currentPath = event.srcElement.location.pathname;
      if (
        PATHS_ARRAY_EXCLUDE_CHECK_UNSAVED_DATA.some(
          (x) => x.toLowerCase() === currentPath.toLowerCase()
        )
      ) {
        return;
      }
      return "Changes that you made may not be saved.";
    };

    window.addEventListener("acceptedCookie-localstorage-changed", (event) => {
      this.acceptedCookie = event.detail.storage;
    });
  },
};
</script>

<style lang="scss">
#hs-web-sdk-iframe-launcher {
  z-index: 3 !important;
}
</style>
